import { useEffect, useRef, useState } from "react";

/**
 * Custom architectural cursor — a small outlined circle that expands and
 * turns dark green with an "OPEN" label when hovering project targets.
 * Targets opt in via [data-cursor="open"] or [data-cursor="link"].
 */
export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const raf = useRef(null);
  const [variant, setVariant] = useState("default");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return;
    setEnabled(true);

    const move = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      }
      const el = e.target.closest?.("[data-cursor]");
      if (el) setVariant(el.getAttribute("data-cursor"));
      else setVariant("default");
    };

    const loop = () => {
      ring.current.x += (pos.current.x - ring.current.x) * 0.16;
      ring.current.y += (pos.current.y - ring.current.y) * 0.16;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ring.current.x}px, ${ring.current.y}px, 0)`;
      }
      raf.current = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", move, { passive: true });
    raf.current = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      cancelAnimationFrame(raf.current);
    };
  }, []);

  if (!enabled) return null;

  const isOpen = variant === "open";
  const isLink = variant === "link";

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]" aria-hidden>
      {/* center dot */}
      <div
        ref={dotRef}
        className="fixed left-0 top-0 -ml-[2px] -mt-[2px]"
        style={{ opacity: isOpen ? 0 : 1, transition: "opacity 300ms var(--ama-ease)" }}
      >
        <div
          className="h-1 w-1 rounded-full"
          style={{ background: isLink ? "#1B3818" : "#000" }}
        />
      </div>

      {/* outer ring */}
      <div ref={ringRef} className="fixed left-0 top-0">
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: isOpen ? 80 : isLink ? 44 : 28,
            height: isOpen ? 80 : isLink ? 44 : 28,
            marginLeft: isOpen ? -40 : isLink ? -22 : -14,
            marginTop: isOpen ? -40 : isLink ? -22 : -14,
            border: `1px solid ${isOpen ? "#1B3818" : "#000"}`,
            background: isOpen ? "#1B3818" : "transparent",
            transition:
              "width 500ms var(--ama-ease), height 500ms var(--ama-ease), margin 500ms var(--ama-ease), background-color 400ms var(--ama-ease), border-color 400ms var(--ama-ease)",
          }}
        >
          <span
            className="ama-label"
            style={{
              color: "#fff",
              fontSize: "0.55rem",
              opacity: isOpen ? 1 : 0,
              transition: "opacity 300ms var(--ama-ease)",
            }}
          >
            Open
          </span>
        </div>
      </div>
    </div>
  );
}
